import { Col, Section } from "@/components";
import { SectionLink } from "@anoki/fse-common";
import { PropsWithChildren, ReactNode, ComponentType, useMemo } from "react";
import "./RichText.scss";
import { TypographyKeys } from "@theme/typography.ts";
import { HTML } from "../HTMLBalancer";
import React from "react";
import clsx from "clsx";

type RichTextProps = PropsWithChildren<{
  title?: ReactNode;
  description?: ReactNode;
  links?: SectionLink[];
  asLink?: "a" | ComponentType<any>;
  fontSize?: TypographyKeys;
  mdFontSize?: TypographyKeys;
  classNameTitle?: string;
  classNameDescription?: string;
  additionalClass?: string;
  maxLines?: number;
  useShowToggle?: boolean;
}>;

export const RichText = ({
  title,
  description,
  fontSize = "2",
  mdFontSize = "2",
  classNameTitle,
  classNameDescription,
  additionalClass,
  children,
  maxLines = undefined,
  useShowToggle = true,
}: RichTextProps) => {
  const [clamped, setClamped] = React.useState<boolean>(
    typeof maxLines === "number",
  );
  const [showToggle, setShowToggle] = React.useState(false);

  const contentRef = React.useRef<HTMLDivElement>(null);

  const toggleClamped = () => {
    setClamped(!clamped);
  };

  React.useLayoutEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const { scrollHeight, clientHeight } = contentRef.current;
        setShowToggle(scrollHeight > clientHeight);
      }
    };

    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [description, maxLines]);

  const body = useMemo(() => {
    if (!description) return null;
    if (
      (typeof description === "string" && React.isValidElement(description)) ||
      !maxLines
    ) {
      return HTML(description.toString(), "div");
    } else {
      return (
        <>
          <div
            ref={contentRef}
            className={clsx(
              "rich-text-body",
              clamped && "rich-text-body--clamped",
              classNameDescription
            )}
            style={{ "--max-lines": maxLines } as React.CSSProperties}
          >
            {HTML(description.toString(), "div")}
          </div>

          {useShowToggle && showToggle && (
            <a
              aria-label="Mostra altro"
              className="toggle-clamped-btn"
              onClick={toggleClamped}
            >
              Mostra {clamped ? "altro" : "meno"}
            </a>
          )}
        </>
      );
    }
  }, [description, clamped, maxLines, showToggle]);

  return (
    <Section className={clsx("base-col ui-rich-text-section", additionalClass)}>
      <Col className="ui-rich-text-content">
        {title && (
          <Section.Title
            fs={fontSize}
            mdFs={mdFontSize}
            className={classNameTitle}
          >
            {title}
          </Section.Title>
        )}
        <Section.Description className={classNameDescription}>{body}</Section.Description>
      </Col>
      {children}
    </Section>
  );
};
